/** Synthetic Inbox list id — opens FoxChat daily check, not a Firestore conversation. */
export const FOX_DAILY_CHECK_INBOX_ID = '__fox_daily_check__';

/**
 * Strings + ids for the reusable Fox chat collage builder.
 * `domIdPrefix` / `messageIdPrefix` keep multiple chat instances from clashing in the DOM.
 */
export const DEFAULT_FOX_CHAT_COPY = {
  firstMessageText: "Hey! Let's set up your visual-vibe transformer",
  firstMessageAlgoIcon: true,
  brandPromptText: 'What stuff do you like? Pick 3 tiles:',
  emojiPromptText: 'Good choice. What emojis show your vibe? Pick 3:',
  placesPromptText: 'Now pick 3 places in the Bay Area:',
  gifPromptText: 'Pick one GIF:',
  nicknamePromptText:
    "Let's pick your nickname!",
  genderPromptText: 'Pick your gender:',
  luvPromptText: 'Are you looking for friends, romance, or maybe both?',
  romanticLookingPromptText: 'For romantic matches, who are you looking for?',
  agePromptText: 'How old are you?',
  locationPromptText: 'Where are you based?',
  locationOtherPlaceholder: 'Other...',
  locationOtherInputAriaLabel: 'Type your city',
  photoPromptText: "Let's pick your photo, and your visual-vibe transformer is ready!",
  photoCollageIntroText:
    "Here you are! Scroll back up and tweak anything you want.",
  /**
   * When true: don't run the fox-mask AI edit during the photo-upload step. Instead show the
   * collage with the user's unmasked photo, and generate + persist the mask in the background
   * AFTER the user taps SAVE (so the chat flow isn't blocked on the slow edit API). When false
   * (opps flow): keep masking inline at upload time.
   */
  deferFoxMaskToSave: true,
  /** Hint under the SAVE button explaining the deferred mask (deferred flow only; null hides it). */
  collageFoxMaskSaveHint:
    "Let's find your vibe match! We'll mask your face with a fox before making any intros.",
  messageInputEnabled: false,
  initialGreetingId: 'fox-greeting',
  domIdPrefix: 'fox-chat',
  messageIdPrefix: 'fox',
  analyticsSource: 'foxchat',
  tileHintPickNickname: '^ pick your nickname ^',
  tileHintPickGender: '^ pick your gender ^',
  tileHintConfirmAge: '^ confirm your age ^',
  tileHintPickLocation: '^ pick your location ^',
  tileHintPickPhoto: '^ pick your photo^',
  selectedBarAriaLabel: 'Your selections',
  saveOpeningCollagesHint: 'Opening your collages...',
  /** Shown when signup (or a reset email) attached the collage and the browser stays signed out. */
  emailSignupSavedHint: 'Saved! Check your email.',
  /** Shown after SAVE attaches the collage to an email account created earlier in this tab. */
  signedOutProfileSaveHint: 'Saved to your profile. Check your email to set your password.',
  nicknamePlaceholder: 'Type your own nickname...',
  nicknameInputAriaLabel: 'Type your own nickname',
  photoChipAriaLabel: 'Remove photo',
  photoChipTitle: 'Your fox-masked photo',
  /** When true, hide gender / LUV / romantic match / age steps and go straight to photo after nickname. */
  skipGenderLuvAgeSteps: false,
  /** Primary CTA on the final collage bubble (signed in). */
  collageCtaSignedIn: 'SAVE COLLAGE',
  /** Primary CTA when signed out (tap opens signup). */
  collageCtaSignedOut: 'SAVE',
  /**
   * When true: after a successful save, stay in chat and open the ops send modal instead of
   * invoking `onCollageSaved` (no HomePage redirect). Re-tapping the CTA reopens the modal.
   */
  opsSendToOpsFlow: false,
  /** Thread header + launcher accessibility label. */
  chatHeaderTitle: 'Vibe Fox',
  /** Self-collage thread header when the user is signed in (GIF daily check). */
  loggedInChatHeaderTitle: 'Vibe Fox',
  /** Opening line in the signed-in daily check thread. */
  dailyCheckGreetingText: 'How are you today?',
  /** Hint above the streak counter in the signed-in daily check thread. */
  dailyCheckSubtitleText:
    'pick today\'s vibe GIF and get a match',
  /** Fox reply after the user sends today's daily-check GIF. */
  dailyCheckComputingReplyText:
    'Updating your VVTF reader and computing a new match...',
  /** Fox reply when a fresh VVTF match conversation was initiated after the daily check. */
  dailyCheckMatchFoundText: 'Found someone!',
  /** CTA under the match-found reply — opens the Messages inbox. */
  dailyCheckMatchFoundCtaText: 'OPEN MESSAGES',
  /** Fox reply when the user's profile is not verified — no match attempted. */
  dailyCheckVerifyGateText: 'Vibes added! Verify your profile to get a vibe match:',
  /** Fox reply when the user is eligible but the engine found no new match today. */
  dailyCheckNoMatchesText: 'No new matches today. Check again tomorrow!',
  /** CTA under the verify-gate reply — reopens the school-email popup if they dismissed it. */
  dailyCheckVerifyGateCtaText: 'VERIFY PROFILE',
  /** Messages inbox row preview after the user has responded today. */
  dailyCheckComputingPreviewText:
    'Updating your VVTF analysis and computing a new match...',
  chatLauncherAriaLabel: 'Chat with Vibe Fox',
};

/** “Collage your opps” variant — anonymous-send flow; same survey machinery as self collage. */
export const FOX_OPS_CHAT_COPY = {
  ...DEFAULT_FOX_CHAT_COPY,
  firstMessageText:
    "Hey! Let's collage YOUR OPPS. Once it's done, you can send it to them anonymously.",
  brandPromptText: 'What stuff do they hate? Pick 3 tiles:',
  emojiPromptText: 'Good choice. What emojis annoy them? Pick 3:',
  placesPromptText: "Now pick the 3 places they can't stand:",
  gifPromptText: 'Pick one GIF to upset them:',
  nicknamePromptText:
    "Let's pick their nickname.",
  genderPromptText: 'Their gender:',
  luvPromptText: 'Friend-group friction, romantic tension, or both?',
  romanticLookingPromptText: 'If romance is in the mix, who are they usually into?',
  agePromptText: 'How old are they?',
  locationPromptText: 'Where are they based?',
  locationOtherPlaceholder: 'Other...',
  locationOtherInputAriaLabel: 'Type their city',
  photoPromptText: 'Upload their least fave pic:',
  photoCollageIntroText:
    "Here's their opps collage! Scroll back up to tweak anything before you save or send.",
  initialGreetingId: 'fox-ops-greeting',
  domIdPrefix: 'fox-ops-chat',
  messageIdPrefix: 'fox-ops',
  analyticsSource: 'foxchat_ops',
  tileHintPickNickname: '^ pick a nickname ^',
  tileHintPickGender: '^ pick their gender ^',
  tileHintConfirmAge: '^ confirm their age ^',
  tileHintPickLocation: '^ pick their location ^',
  tileHintPickPhoto: '^ pick their photo ^',
  selectedBarAriaLabel: 'Opps collage selections',
  saveOpeningCollagesHint: 'Opening your collages...',
  nicknamePlaceholder: 'Type a nickname...',
  nicknameInputAriaLabel: 'Type a custom nickname',
  photoChipAriaLabel: 'Remove photo',
  photoChipTitle: 'Their fox-masked photo',
  skipGenderLuvAgeSteps: true,
  /* Opps flow keeps masking inline at upload (the send-to-opps path has no background save doc to
     patch later), and shows no deferred-mask hint. */
  deferFoxMaskToSave: false,
  collageFoxMaskSaveHint: null,
  collageCtaSignedIn: 'SEND TO YOUR OPPS',
  collageCtaSignedOut: 'SIGN UP TO SEND',
  opsSendToOpsFlow: true,
  chatHeaderTitle: 'The Antifox',
  chatLauncherAriaLabel: 'Chat with The Antifox',
};
